import {Injectable} from '@angular/core';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk'
import {fetchAuthSession, getCurrentUser} from 'aws-amplify/auth';
import {datadogRum} from '@datadog/browser-rum';

@Injectable({
  providedIn: 'root'
})
export class LaunchDarklyService {
  private launchDarklyKey: string = "";
  private context: LaunchDarkly.LDContext | null = null;
  private client : LaunchDarkly.LDClient | null = null;
  private isClientReady: boolean = false;
  private flagCache: Map<string, boolean> = new Map();
  constructor() {
    this.initializeClient();
  }

  private async initializeClient() {

    this.setLaunchDarklyKey();
    try {
    const { username } = await getCurrentUser();
    const sessions = await fetchAuthSession();
    const idTokenPayload = sessions.tokens?.idToken?.payload;

    this.context = {
      kind: "user",
      key: username,
      email: idTokenPayload?.["email"],
    }
    } catch (error) {
      this.context = {
        kind: "user",
        key: this.generateRandomKey(),
      }
    }

    const options: LaunchDarkly.LDOptions = {
      inspectors: [
        {
          type: "flag-used",
          name: "dd-inspector",
          method: (key: string, detail: LaunchDarkly.LDEvaluationDetail) => {
            datadogRum.addFeatureFlagEvaluation(key, detail.value);
          },
        },
      ]
    };
    this.client = LaunchDarkly.initialize(this.launchDarklyKey, this.context, options);

    this.client.on("initialized", () => {
      console.log("LaunchDarkly SDK successfully initialized!");
      this.isClientReady = true;
    })
  }

  private setLaunchDarklyKey() {
    this.launchDarklyKey = "682c3390aa2ecf089f537dc9";
  }

  private generateRandomKey() {
    return "anon-" + Math.random().toString(36).substring(2, 15);
  }

  private async waitForClientInitialization() : Promise<void> {
    if (!this.isClientReady) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.client?.on("initialized", () => {
        console.log("LaunchDarkly SDK successfully initialized!");
        this.isClientReady = true;
        resolve();
      })
    })
  }

  async isFeatureEnabled(featureKey: string): Promise<boolean> {
    console.log("LaunchDarkly SDK successfully enabled!");

    if(this.flagCache.has(featureKey)) {
      return this.flagCache.get(featureKey)!;
    }
    await this.waitForClientInitialization();

    const variation = this.client?.variation(featureKey, false) ?? false;
    this.flagCache.set(featureKey, variation);
    return variation;
  }

  async getFeatureFlags(flagsKeys: string[]): Promise<Record<string, boolean>> {
    console.log("LaunchDarkly SDK successfully getFeatureFlags!");

    await this.waitForClientInitialization();

    const results: Record<string, boolean> = {};
    for(const flag of flagsKeys) {
      if(this.flagCache.has(flag)) {
        results[flag] = this.flagCache.get(flag)!;
      } else {
        const variation = this.client?.variation(flag, false) ?? false;
        this.flagCache.set(flag, variation);
        results[flag] = variation;
      }
    }

    console.log("LaunchDarkly SDK successfully getFeatureFlags: ", results);
    return results;
  }

  updateUserContext(kind: string, key: string | undefined, email: string) {
    this.context = {kind: kind, key: key, email: email};
    this.client = LaunchDarkly.initialize(this.launchDarklyKey, this.context);
    this.isClientReady = false;

    this.client.on("initialized", () => {
      console.log("LaunchDarkly SDK successfully initialized!");
      this.isClientReady = true;
    });
  }

  closeClient() {
    if(this.client) {
      this.client.close();
      this.client = null;
      this.isClientReady = false;
    }
  }

  initializeLaunchDarkly() {
    this.setLaunchDarklyKey();

    if (!this.launchDarklyKey) {
      console.error("LaunchDarkly key is missing. Please provide a valid client-side ID.");
      return;
    }

    this.client = LaunchDarkly.initialize(this.launchDarklyKey, { key: this.generateRandomKey() });

    this.client.on('ready', () => {
      console.log("LaunchDarkly SDK successfully initialized!");
      this.isClientReady = true;
    });
  }
}
